import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { ExtractionHandlerContext, FieldChangeEvent } from '@alliance-strategies/form-engine-portable';
import { createPassportProvenanceTracker, hashTrackedValues, isPassportStepKey, type ProvenanceSnapshot } from '../lib/passportProvenance';
import type { FormEngineHelpers } from '../lib/formEngineHelpers';
import {
  createPassportExtractionAdapter,
  type PassportExtractionApplied,
} from '../lib/passportExtractionAdapter';
import { canonicalPassportTargets } from '../lib/passportCanonicalExtraction';
import { extractPassport } from '../lib/portal';
import { track } from '../lib/analytics';

type Options = {
  appId: string | number;
  stepKey: string;
  /** Stable dependent/draft identity; changing it ends the prior host lifetime. */
  contextKey?: unknown;
  /** The same public served fieldsConfig passed to the renderer. */
  fieldsConfig?: unknown;
  readOnly?: boolean;
  onErrorNotice: (notice: string | undefined) => void;
  onApplied?: (result: PassportExtractionApplied, helpers: FormEngineHelpers, isCurrent: () => boolean) => void;
};

/**
 * Passport state belongs to one application/step, not to an arbitrary render.
 * Metadata callbacks remain stable while the tracker evolves; context and
 * notification callbacks are read from the current render when OCR completes.
 * Changing identity invalidates the old factory even before effect cleanup.
 */
export function usePassportCapture(options: Options) {
  const { appId, stepKey, contextKey } = options;
  const { nationality, dateOfBirth } = canonicalPassportTargets(options.fieldsConfig);
  // Schema changes end capture ownership, but retain already observed manual
  // evidence (including old aliases) in this application/step's tracker.
  const lifetime = useMemo(() => ({
    appId, stepKey, contextKey, provenance: createPassportProvenanceTracker(),
  }), [appId, stepKey, contextKey]);
  const scope = useMemo(() => ({
    ...lifetime,
    canonicalTargets: { nationality, dateOfBirth },
    enabled: isPassportStepKey(lifetime.stepKey),
    mounted: true,
    form: undefined as unknown,
    formGeneration: 0,
    removalGeneration: 0,
    fieldListener: undefined as ((event: FieldChangeEvent) => void) | undefined,
  }), [lifetime, nationality, dateOfBirth]);
  const current = useRef({ options, scope });
  current.current = { options, scope };
  useEffect(() => {
    scope.mounted = true;
    return () => {
      scope.mounted = false;
      scope.provenance.invalidateExtractions();
    };
  }, [scope]);

  const isCurrent = useCallback(
    () => scope.mounted && current.current.scope === scope,
    [scope],
  );
  const draftMetaFor = useCallback(
    (values: Record<string, unknown>) => scope.enabled ? scope.provenance.snapshot(values) : null,
    [scope],
  );
  const reconcileFormValues = useCallback((snapshot: Record<string, unknown>) => {
    if (!isCurrent()) return undefined;
    const form = scope.form as { getValues: () => Record<string, unknown> } | undefined;
    if (!form) return { ...snapshot };
    // A synchronous field-event listener can edit RHF before an outer watch
    // delivers its older snapshot. Read the settled public form, never use that
    // stale echo to replace either the draft ref or the provenance baseline.
    const values = { ...form.getValues() };
    // Match the engine's values-only callback contract, not a new photo writer.
    delete values.profilePicture;
    return values;
  }, [scope, isCurrent]);
  const createFactory = useCallback((helpers: FormEngineHelpers) => {
    return (context: ExtractionHandlerContext) => {
      if (isCurrent() && scope.form !== context.form) {
        if (scope.form !== undefined) scope.provenance.invalidateExtractions();
        scope.formGeneration++;
        scope.removalGeneration++;
        scope.form = context.form;
      }
      const formGeneration = scope.formGeneration;
      const isFormCurrent = () => isCurrent() && scope.formGeneration === formGeneration;
      // Pre-#373 adapter: needs only {form, setExtractingByField}, matching what
      // the engine actually hands the factory. The repaired adapter this
      // replaced required ctx.passportExtractionScope/fieldChangeScope, which
      // the installed engine never populates -- that threw synchronously before
      // any extraction request could fire (Asana 1218694169667926).
      const factory = createPassportExtractionAdapter({
        appId, stepKey, helpers,
        provenance: scope.provenance,
        extract: extractPassport,
        isActive: () => isFormCurrent() && !current.current.options.readOnly,
        onErrorNotice: (notice) => {
          if (isFormCurrent()) current.current.options.onErrorNotice(notice);
        },
        onApplied: (result) => {
          if (isFormCurrent()) current.current.options.onApplied?.(result, helpers, () => isFormCurrent() && result.isCurrent());
        },
        emit: track,
      });
      const handler = factory({ form: context.form, setExtractingByField: context.setExtractingByField });
      return (...args: Parameters<typeof handler>) => {
        if (!isFormCurrent()) return Promise.resolve();
        scope.removalGeneration++;
        scope.provenance.finishDocumentRemoval();
        return handler(...args);
      };
    };
  },
  [appId, stepKey, scope, isCurrent]);

  const onFieldChange = useCallback((event: FieldChangeEvent) => {
    if (!isCurrent() || !scope.enabled) return;
    if (scope.fieldListener) scope.fieldListener(event);
    else scope.provenance.observeFieldChange(event);
  }, [scope, isCurrent]);

  const onDocumentRemoved = useCallback((latestValues: Record<string, unknown>) => {
    if (!isCurrent() || current.current.options.readOnly) return;
    const form = scope.form as {
      getValues?: () => Record<string, unknown>;
      setValue?: (field: string, value: unknown, options?: { shouldDirty: boolean }) => void;
    } | undefined;
    const before = { ...(form?.getValues?.() ?? latestValues) };
    const saved = scope.provenance.snapshot(before);
    const retained = Object.entries(saved.fields).filter(([field, meta]) =>
      meta.origin !== 'ocr' && before[field] != null && before[field] !== '');
    const generation = ++scope.removalGeneration;
    scope.provenance.beginDocumentRemoval();
    // AdvancedFileUpload clears all passport values synchronously AFTER
    // onDeleteDocument. Restore existing corrections through the public RHF
    // form in the registry context, not renderer internals or invented events.
    queueMicrotask(() => {
      if (!isCurrent() || generation !== scope.removalGeneration) return;
      try {
        if (current.current.options.readOnly) return;
        if (!form?.setValue || !form.getValues) {
          if (retained.length) current.current.options.onErrorNotice('Please re-enter your passport corrections after removing the document.');
          return;
        }
        scope.provenance.observeValues(form.getValues());
        scope.provenance.finishDocumentRemoval();
        const restoredFields: ProvenanceSnapshot['fields'] = {};
        for (const [field, metadata] of retained) {
          const value = form.getValues()[field];
          if (value != null && value !== '') continue; // never replace a newer edit
          form.setValue(field, before[field], { shouldDirty: true });
          if (form.getValues()[field] === before[field]) restoredFields[field] = metadata;
        }
        const restored = form.getValues();
        const snapshot = scope.provenance.snapshot(restored);
        scope.provenance.hydrate(restored, {
          ...snapshot, valuesHash: hashTrackedValues(restored),
          fields: { ...snapshot.fields, ...restoredFields },
        });
      } catch {
        current.current.options.onErrorNotice('Your passport corrections could not be restored after removing the document. Please check the fields before saving.');
      } finally {
        scope.provenance.finishDocumentRemoval();
      }
    });
  }, [scope, isCurrent]);

  return {
    provenance: scope.provenance,
    tracksProvenance: scope.enabled,
    draftMetaFor,
    createFactory,
    isCurrent,
    onDocumentRemoved,
    onFieldChange,
    reconcileFormValues,
  };
}